import { forwardRef, type InputHTMLAttributes, useId } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  description?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, description, className, id: providedId, ...props }, ref) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const descriptionId = description ? `${id}-description` : undefined;
    const errorId = error ? `${id}-error` : undefined;

    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-gray-700"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          aria-describedby={cn(descriptionId, errorId)}
          aria-invalid={error ? 'true' : undefined}
          className={cn(
            // Base styles
            'block w-full rounded-lg px-3.5 py-2.5',
            'text-gray-900 text-sm',
            'placeholder:text-gray-400',
            // Border and background
            'bg-white border',
            error
              ? 'border-status-critical focus:border-status-critical focus:ring-status-critical/20'
              : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500/20',
            // Focus state
            'focus:outline-none focus:ring-4',
            'transition-all duration-150',
            // Disabled state
            'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
            className
          )}
          {...props}
        />
        {description && !error && (
          <p id={descriptionId} className="text-sm text-gray-500">
            {description}
          </p>
        )}
        {error && (
          <p id={errorId} className="text-sm text-status-critical font-medium">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
